import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import formatBytes from '../../../shared/utils/formatBytes';
import { formatDateTime } from '../../../shared/utils/formatDate';
import MediaLibraryGrid from './MediaLibraryGrid';
import { usageLabel } from './usageLabel';

export default function MediaLibraryPanel({
  items,
  loading,
  error,
  fetchDownloadUrl,
  fetchUsages,
  onDelete,
  onPickFiles,
  onUpload,
  uploading,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [usagesById, setUsagesById] = useState({});
  const [usagesLoadingId, setUsagesLoadingId] = useState(null);
  const [usagesError, setUsagesError] = useState(null);

  useEffect(() => {
    if (!expandedId || usagesById[expandedId]) return;

    let cancelled = false;
    setUsagesLoadingId(expandedId);
    setUsagesError(null);

    fetchUsages(expandedId)
      .then((usages) => {
        if (!cancelled) {
          setUsagesById((prev) => ({ ...prev, [expandedId]: usages }));
        }
      })
      .catch((err) => {
        if (!cancelled) setUsagesError(err.message || 'Failed to load usages');
      })
      .finally(() => {
        if (!cancelled) setUsagesLoadingId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [expandedId, fetchUsages, usagesById]);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.filename}" from the media library?`)) return;
    try {
      await onDelete(item.id);
      if (expandedId === item.id) setExpandedId(null);
    } catch {
      // error surfaced by parent hook
    }
  };

  const readyItems = items.filter((item) => item.status === 'ready');

  return (
    <section className="mb-4">
      <MediaLibraryGrid
        items={readyItems}
        loading={loading}
        error={error}
        fetchDownloadUrl={fetchDownloadUrl}
        onSelect={(item) => setExpandedId((current) => (current === item.id ? null : item.id))}
        selectedId={expandedId}
        onPickFiles={onPickFiles}
        onUpload={onUpload}
        uploading={uploading}
      />

      {expandedId && (
        <div className="card mt-3">
          <div className="card-body">
            {(() => {
              const item = readyItems.find((entry) => entry.id === expandedId);
              if (!item) return null;

              const usages = usagesById[item.id] || [];

              return (
                <>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                      <div className="fw-semibold">{item.filename}</div>
                      <div className="small text-muted">
                        {`${formatBytes(item.byte_size)} · ${formatDateTime(item.created_at)}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </button>
                  </div>

                  <div className="small fw-semibold mb-2">{usageLabel(usages.length)}</div>
                  {usagesLoadingId === item.id && (
                    <div className="text-muted small">Loading usage…</div>
                  )}
                  {usagesError && (
                    <div className="alert alert-danger py-2 small mb-0">{usagesError}</div>
                  )}
                  {!usagesLoadingId && !usagesError && usages.length === 0 && (
                    <div className="text-muted small">Not used in any workflow yet.</div>
                  )}
                  {!usagesLoadingId && usages.length > 0 && (
                    <ul className="small mb-0 ps-3">
                      {usages.map((usage) => (
                        <li key={`${usage.workflow_id}-${usage.workflow_step_id}-${usage.usage_kind}`}>
                          <Link to={`/admin/flow/${usage.workflow_id}`}>
                            {usage.workflow_name}
                          </Link>
                          {' '}
                          <span className="text-muted">
                            ({usage.workflow_state}, step {usage.workflow_step_id}, {usage.usage_kind})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
}

MediaLibraryPanel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  fetchDownloadUrl: PropTypes.func.isRequired,
  fetchUsages: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPickFiles: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
};

MediaLibraryPanel.defaultProps = {
  loading: false,
  error: null,
  uploading: false,
};
